import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  accountTypeOptions,
  MARKUP_DEFAULTS_BY_CONFIGURATION_TYPE,
  MARKUP_KINDS,
  MarkupKind,
  truckingProviderOptions,
} from '@pelicargo/utils';
import { LineItemAudience, MarkupConfigurationType } from '@prisma/client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { HiChevronDown, HiOutlineCurrencyDollar, HiPlus } from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { MARKUP_KIND_DETAILS, MarkupCard } from './MarkupCard';

const KINDS: MarkupKind[] = ['AIR', 'INLAND'];

const TYPE_OPTIONS = [
  { value: MarkupConfigurationType.SYSTEM, label: 'System', description: 'Platform-wide markup' },
  { value: MarkupConfigurationType.TENANT, label: 'Tenant', description: "Applies to this tenant's quotes" },
  {
    value: MarkupConfigurationType.FULFILLMENT_PARTNER,
    label: 'Fulfillment Partner',
    description: 'Applies to one fulfillment partner account',
  },
];

type Audiences = { child_itemized_for?: LineItemAudience[] };

// "Show to customer" is the markup's own choice, so it survives a change of configuration type
const withTypeDefaults = <T extends Audiences>(markup: T, type: MarkupConfigurationType) => {
  const defaults = MARKUP_DEFAULTS_BY_CONFIGURATION_TYPE[type];
  const isShownToCustomer = (markup.child_itemized_for ?? []).includes(LineItemAudience.CUSTOMER);

  return {
    ...markup,
    category: defaults.category,
    child_itemized_for: [...defaults.child_itemized_for, ...(isShownToCustomer ? [LineItemAudience.CUSTOMER] : [])],
    child_excluded_for: [...defaults.child_excluded_for],
  };
};

const newMarkup = (kind: MarkupKind, type: MarkupConfigurationType) =>
  withTypeDefaults(
    {
      label: `${MARKUP_KIND_DETAILS[kind].label} Markup`,
      min_cost: null,
      costs: [{ min: 0, cost: null }],
      line_item_types: [...MARKUP_KINDS[kind].line_item_types],
      applicable_freight_services: [...MARKUP_KINDS[kind].applicable_freight_services],
      child_itemized_for: [] as LineItemAudience[],
    },
    type,
  );

type ConditionName = 'origin_airports' | 'providers' | 'account_as_requestors' | 'account_types' | 'trucking_providers';

type ConditionProps = {
  name: ConditionName;
  label: string;
  children: ReactNode;
};

/**
 * "Apply to all X". The server treats an empty list as "allow all", so checking the box
 * clears the list; unchecking reveals the picker to narrow it.
 */
const Condition = ({ name, label, children }: ConditionProps) => {
  const { setValue } = useFormContext();
  const values: unknown[] = useWatch({ name }) ?? [];
  const [isNarrowing, setIsNarrowing] = useState(false);

  const isAll = !values.length && !isNarrowing;

  const handleChange = (isChecked: boolean) => {
    setIsNarrowing(!isChecked);
    if (isChecked) setValue(name, [], { shouldDirty: true });
  };

  return (
    <Stack spacing="2">
      <Checkbox isChecked={isAll} onChange={(event) => handleChange(event.target.checked)}>
        {label}
      </Checkbox>
      <Show if={!isAll}>
        <Box pl="6">{children}</Box>
      </Show>
    </Stack>
  );
};

const countOf = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

type Props = {
  /** When opened from an account, the configuration is that account's FP configuration */
  lockedFulfillmentPartnerId?: number;
};

/** The markup configuration form (PEL-3045). `ClassicMarkupConfigurationForm` is the old one. */
export const MarkupConfigurationForm = ({ lockedFulfillmentPartnerId }: Props) => {
  const { knownTenants, isSuperAdminOrSystemTenant } = useAuth();
  const { setValue, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'markups' });

  const type: MarkupConfigurationType = useWatch({ name: 'type' });
  const tenantId = useWatch({ name: 'tenant_id' });
  const isRestricted: boolean = useWatch({ name: 'is_restricted' }) ?? false;
  const origins: string[] = useWatch({ name: 'origin_airports' }) ?? [];
  const providers: string[] = useWatch({ name: 'providers' }) ?? [];
  const customers: number[] = useWatch({ name: 'account_as_requestors' }) ?? [];
  const accountTypes: string[] = useWatch({ name: 'account_types' }) ?? [];
  const truckingProviders: string[] = useWatch({ name: 'trucking_providers' }) ?? [];

  // Expanded cards, by field id. Saved markups open collapsed; a newly added one opens.
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const { data: tenantAccounts } = trpc.getAllAccounts.useQuery(
    { tenant_id: tenantId },
    { enabled: !!tenantId, refetchOnWindowFocus: false },
  );
  // Any account can be a fulfillment partner, whichever tenant it's quoted under
  const { data: allAccounts } = trpc.getAllAccounts.useQuery(undefined, {
    enabled: isSuperAdminOrSystemTenant,
    refetchOnWindowFocus: false,
  });
  const customerOptions = tenantAccounts?.map((account) => ({ label: account.name, value: String(account.id) }));
  const partnerOptions = allAccounts?.map((account) => ({ label: account.name, value: String(account.id) }));
  const tenantOptions = knownTenants.map((tenant) => ({ label: tenant, value: tenant }));

  // Only Peli's own admins pick the type/tenant or touch FP configurations; whitelabel tenants
  // only ever manage their own Tenant markups
  const isPartnerConfiguration = type === MarkupConfigurationType.FULFILLMENT_PARTNER;
  const showTypeAndTenant = isSuperAdminOrSystemTenant;
  const showPartner = isSuperAdminOrSystemTenant && isPartnerConfiguration;
  const showAccountTypes = isSuperAdminOrSystemTenant;
  const isLocked = !!lockedFulfillmentPartnerId;

  // A changed type re-stamps every markup's category/audiences and drops FP-only settings.
  // Hydration (undefined -> type) isn't a change.
  const previousType = useRef(type);
  useEffect(() => {
    const hasChanged = !!previousType.current && previousType.current !== type;
    previousType.current = type;
    if (!hasChanged || !type) return;

    const markups: Audiences[] = getValues('markups') ?? [];
    setValue(
      'markups',
      markups.map((markup) => (markup ? withTypeDefaults(markup, type) : markup)),
      { shouldDirty: true },
    );
    if (type !== MarkupConfigurationType.FULFILLMENT_PARTNER) {
      setValue('account_as_fulfillment_partner_id', null, { shouldDirty: true });
      setValue('trucking_providers', [], { shouldDirty: true });
      setValue('is_restricted', false, { shouldDirty: true });
    }
  }, [type, getValues, setValue]);

  // Picked customers belong to the previous tenant, so they can't carry over
  const previousTenantId = useRef(tenantId);
  useEffect(() => {
    const hasChanged = !!previousTenantId.current && previousTenantId.current !== tenantId;
    previousTenantId.current = tenantId;
    if (hasChanged) setValue('account_as_requestors', [], { shouldDirty: true });
  }, [tenantId, setValue]);

  const narrowed = [
    origins.length ? countOf(origins.length, 'origin', 'origins') : '',
    providers.length ? countOf(providers.length, 'provider', 'providers') : '',
    customers.length ? countOf(customers.length, 'customer', 'customers') : '',
    accountTypes.length ? countOf(accountTypes.length, 'account type', 'account types') : '',
    truckingProviders.length ? countOf(truckingProviders.length, 'trucking provider', 'trucking providers') : '',
  ].filter(Boolean);
  const restriction = isRestricted ? 'Restricted' : 'Not restricted';
  const conditionsSummary = [
    ...(narrowed.length ? narrowed : ['All allowed']),
    ...(isPartnerConfiguration ? [restriction] : []),
  ].join(' · ');

  const hasMarkups = fields.length > 0;

  // `append` only assigns the new field's id on the next render, so open it from there.
  // Hydrating saved markups via `reset` also grows `fields`, which is why this is flagged.
  const isAdding = useRef(false);
  useEffect(() => {
    if (!isAdding.current) return;
    isAdding.current = false;
    const added = fields[fields.length - 1];
    if (added) setExpandedIds((ids) => [...ids, added.id]);
  }, [fields]);

  const handleAdd = (kind: MarkupKind) => () => {
    isAdding.current = true;
    append(newMarkup(kind, type ?? MarkupConfigurationType.TENANT));
  };

  const toggle = (fieldId: string) => () => {
    setExpandedIds((ids) => (ids.includes(fieldId) ? ids.filter((id) => id !== fieldId) : [...ids, fieldId]));
  };

  const handleRemove = (index: number) => () => remove(index);

  const formatTypeOption = (option: (typeof TYPE_OPTIONS)[number], { context }: { context: 'menu' | 'value' }) => {
    if (context === 'value') return option.label;

    return (
      <Stack spacing="0">
        <Text>{option.label}</Text>
        <Text fontSize="xs" color="fg.muted">
          {option.description}
        </Text>
      </Stack>
    );
  };

  return (
    <Stack spacing="6">
      <SimpleInput
        name="label"
        label="Label"
        placeholder="Base"
        helper="Used to identify this configuration in lists and on quotes."
        inputAttrs={{ variant: 'outline', px: 4 }}
      />

      <Show if={showTypeAndTenant}>
        {/* SimpleReactSelect doesn't forward layout props to its field, so the grid sizes the cells */}
        <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="3">
          <SimpleReactSelect
            name="type"
            label="Type"
            options={TYPE_OPTIONS}
            formatOptionLabel={formatTypeOption as any}
            isDisabled={isLocked}
            isOutlined
          />
          <SimpleReactSelect name="tenant_id" label="Tenant" options={tenantOptions} isOutlined />
        </Grid>
      </Show>

      <Show if={showPartner}>
        <SimpleReactSelect
          name="account_as_fulfillment_partner_id"
          label="Fulfillment partner"
          helper={
            isLocked
              ? 'Fixed to this account. Edit from Markup Configurations to change it.'
              : 'The account this configuration is for. Required for Fulfillment Partner configurations.'
          }
          options={partnerOptions}
          placeholder="Search accounts"
          isDisabled={isLocked}
          isSearchable
          isClearable
          isOutlined
        />
      </Show>

      <Stack spacing="3">
        <HStack justify="space-between">
          <Text textStyle="label">Markups · {fields.length}</Text>
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              colorScheme={hasMarkups ? 'gray' : 'brand'}
              variant={hasMarkups ? 'outline' : 'solid'}
              leftIcon={<Icon as={HiPlus} />}
              rightIcon={<Icon as={HiChevronDown} />}
            >
              Add New Markup
            </MenuButton>
            <MenuList p="1" minW="264px">
              {KINDS.map((kind) => {
                const details = MARKUP_KIND_DETAILS[kind];

                return (
                  <MenuItem key={kind} onClick={handleAdd(kind)} borderRadius="base" alignItems="flex-start" gap="3">
                    <Icon as={details.icon} fontSize="18" color="fg.muted" mt="0.5" />
                    <Stack spacing="0.5">
                      <Text fontSize="md" fontWeight="medium">
                        {details.label}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        {details.description}
                      </Text>
                    </Stack>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </HStack>

        <Show if={!hasMarkups}>
          <Stack
            align="center"
            spacing="2"
            py="10"
            px="8"
            textAlign="center"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="border.emphasized"
            borderRadius="lg"
            bg="bg.subtle"
          >
            <Icon as={HiOutlineCurrencyDollar} fontSize="32" color="fg.subtle" />
            <Text fontSize="md" fontWeight="bold" mt="1">
              No markups yet
            </Text>
            <Text fontSize="sm" color="fg.muted" maxW="300px">
              Add an Air or Inland markup. Air and Inland markups are priced separately.
            </Text>
          </Stack>
        </Show>

        {fields.map((field, index) => (
          <MarkupCard
            key={field.id}
            prefix={`markups.${index}`}
            isExpanded={expandedIds.includes(field.id)}
            onToggle={toggle(field.id)}
            onRemove={handleRemove(index)}
          />
        ))}
      </Stack>

      <Accordion allowToggle mx="-6" borderTopWidth="1px">
        <AccordionItem border="0">
          {({ isExpanded: isAdvancedOpen }) => (
            <>
              <AccordionButton px="6" py="3" gap="2">
                <AccordionIcon />
                <Text fontSize="md" fontWeight="semibold">
                  Advanced
                </Text>
                <Show if={!isAdvancedOpen}>
                  <Text ml="auto" fontSize="sm" color="fg.muted" noOfLines={1}>
                    {conditionsSummary}
                  </Text>
                </Show>
              </AccordionButton>
              <AccordionPanel px="6" pb="4">
                <Stack spacing="4">
                  <Text fontSize="sm" color="fg.muted">
                    These conditions apply to every markup in this configuration. Uncheck one to limit it to specific
                    values.
                  </Text>
                  <Condition name="origin_airports" label="Apply to all origins">
                    <AirportInput name="origin_airports" placeholder="LAX, JFK, ORD" isMulti isOutlined />
                  </Condition>
                  <Condition name="providers" label="Apply to all providers">
                    <AirlineInput
                      name="providers"
                      placeholder="AC, UA, EK"
                      helper="Providers (Airlines, Warehouses, and Trucking companies)."
                      isMulti
                      isOutlined
                    />
                  </Condition>
                  <Condition name="account_as_requestors" label="Apply to all customers">
                    <SimpleReactSelect
                      name="account_as_requestors"
                      options={customerOptions}
                      placeholder="Search customers"
                      helper="Only customers in this tenant are listed."
                      isSearchable
                      isMulti
                      isOutlined
                    />
                  </Condition>
                  <Show if={showAccountTypes}>
                    <Condition name="account_types" label="Apply to all account types">
                      <SimpleReactSelect
                        name="account_types"
                        options={accountTypeOptions}
                        placeholder="Add account type"
                        isMulti
                        isOutlined
                      />
                    </Condition>
                  </Show>
                  <Show if={showPartner}>
                    <Condition name="trucking_providers" label="Apply to all trucking providers">
                      <SimpleReactSelect
                        name="trucking_providers"
                        options={truckingProviderOptions}
                        placeholder="Add trucking provider"
                        helper="Fulfillment Partner configurations only."
                        isMulti
                        isOutlined
                      />
                    </Condition>
                    <Stack spacing="1" borderTopWidth="1px" pt="4">
                      <Checkbox
                        isChecked={isRestricted}
                        onChange={(event) => setValue('is_restricted', event.target.checked, { shouldDirty: true })}
                      >
                        Restricted
                      </Checkbox>
                      <Text fontSize="sm" color="fg.muted" pl="6">
                        Invert this configuration: when a result matches these conditions, it is not a valid option.
                      </Text>
                    </Stack>
                  </Show>
                </Stack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </Stack>
  );
};
